/** "Upload import file" dialog — the console counterpart of the dashboard's
 *  Upload button. The console is cross-tenant, so a tenant MUST be picked;
 *  the file is an XML (or JSON — converted server-side) cp-import document,
 *  queued for processing immediately on submit.
 */
import UploadFileIcon from '@mui/icons-material/UploadFile';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import * as importRunsApi from '@/api/importRuns';
import { ConsoleApiError } from '@/api/client';
import { useSnackbar } from '@/components/Snackbar';
import { TenantPicker, type TenantPickerValue } from '@/components/TenantPicker/TenantPicker';
import { useCreateDialogTenant } from '@/scope/useCreateDialogTenant';

export interface UploadImportDialogProps {
  open: boolean;
  onClose: () => void;
  /** The page's operators (D9): the one is pre-selected on every open, all are
   *  featured; see `useCreateDialogTenant`. */
  pageTenants?: TenantPickerValue[];
  /** Route to a run's detail dialog (keeps the grid's query string). */
  detailPath: (id: string) => string;
}

export function UploadImportDialog({ open, onClose, pageTenants, detailPath }: UploadImportDialogProps) {
  const snackbar = useSnackbar();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Seeded on every open (this dialog stays mounted, so a mount-time seed went
  // stale and a Cancelled pick survived).
  const [tenant, setTenant] = useCreateDialogTenant(open, pageTenants);
  const [file, setFile] = useState<File | null>(null);

  // With page tenants every open starts fresh: no file left from a Cancel.
  useEffect(() => {
    if (open && pageTenants !== undefined) setFile(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const mutation = useMutation({
    mutationFn: importRunsApi.upload,
    onSuccess: (res) => {
      snackbar.show(
        `${res.converted ? 'JSON' : 'XML'} file queued for import as run ${res.id.slice(0, 8)}…`,
        'success',
      );
      void queryClient.invalidateQueries({ queryKey: ['import-runs'] });
      onClose();
      navigate(detailPath(res.id));
    },
    onError: (e) => {
      const message = e instanceof ConsoleApiError ? e.message : 'Upload failed.';
      snackbar.show(`Upload failed: ${message}`, 'error');
    },
  });

  const submit = () => {
    if (!file || !tenant) return;
    mutation.mutate({ file, tourOperatorId: tenant.id });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Upload import file</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Alert severity="info">
            The file is a cp-import XML document (JSON is converted automatically) and is
            queued for processing immediately — same as the dashboard&apos;s Upload button.
          </Alert>

          <TenantPicker
            value={tenant}
            featured={pageTenants}
            onChange={setTenant}
            label="Tenant (required)"
          />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Button variant="outlined" startIcon={<UploadFileIcon />} onClick={() => fileInputRef.current?.click()}>
              Choose file
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xml,.json"
              hidden
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
            <Typography variant="body2" color={file ? 'text.primary' : 'text.secondary'}>
              {file ? `${file.name} (${(file.size / 1024).toFixed(1)} KB)` : 'No file selected (.xml or .json)'}
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!file || !tenant || mutation.isPending} onClick={submit}>
          Upload &amp; import
        </Button>
      </DialogActions>
    </Dialog>
  );
}
